import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { AppMenu, AppMenuLink, AppMenuSection } from "design-system/AppShell";

import { usePage } from "../../../contexts/PageContext";
import { useAppUI } from "../../../contexts/UIContext";
import { registerSearchBox } from "../searchBoxes";
import { useFilteredNavTree } from "../useNavItems";
import type { Link, MenuItem } from "./links";
import { filterNavTree } from "./state/navSearch";
import {
  readOpenSections,
  readPins,
  toggleOpen,
  togglePin,
  writeOpenSections,
  writePins,
} from "./state/navPrefs";

interface SidebarProps {
  /** Bumped by the host to focus the search box (the `/` shortcut). */
  focusSearchSignal?: number;
}

/**
 * Side menu — composes the design-system `<AppMenu>` with the project's
 * permission-filtered navigation tree (shared with the navbar page search
 * via `useFilteredNavTree`). Owns the personal state: query, folded
 * sections and pins.
 */
function Sidebar({ focusSearchSignal = 0 }: SidebarProps) {
  const { navigate, page } = usePage();
  const { setSidebarOpen } = useAppUI();
  const tree = useFilteredNavTree();

  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState<string[]>(readOpenSections);
  const [pins, setPins] = useState<string[]>(readPins);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const input = inputRef.current;
    if (!box || !input) return;
    registerSearchBox("menu", { box, input });
    return () => registerSearchBox("menu", null);
  }, []);

  const handledSignal = useRef(0);
  useEffect(() => {
    if (focusSearchSignal <= handledSignal.current) return;
    handledSignal.current = focusSearchSignal;
    inputRef.current?.focus();
  }, [focusSearchSignal]);

  // Landing on a page unfolds its section once; the user may fold it again.
  const activeSectionId = useMemo(
    () => tree.find((s) => s.links.some((l) => l.action === page))?.id,
    [tree, page]
  );
  useEffect(() => {
    if (!activeSectionId) return;
    setOpenIds((prev) => {
      if (prev.includes(activeSectionId)) return prev;
      const next = [...prev, activeSectionId];
      writeOpenSections(next);
      return next;
    });
  }, [activeSectionId]);

  const open = useCallback(
    (link: Link): void => {
      if (link.url) {
        window.open(link.url, "_blank")?.focus();
        return;
      }
      navigate(link.action);
      setQuery("");
      // The page takes the full width; the hamburger brings the menu back.
      setSidebarOpen(false);
    },
    [navigate, setSidebarOpen]
  );

  const onToggleSection = useCallback((id: string): void => {
    setOpenIds((prev) => {
      const next = toggleOpen(prev, id);
      writeOpenSections(next);
      return next;
    });
  }, []);

  const onTogglePin = useCallback((action: string): void => {
    setPins((prev) => {
      const next = togglePin(prev, action);
      writePins(next);
      return next;
    });
  }, []);

  const toMenuLink = useCallback(
    (section: MenuItem, link: Link): AppMenuLink => ({
      id: link.action,
      label: link.name,
      icon: section.icon,
      active: !link.url && link.action === page,
      pinned: pins.includes(link.action),
      tag: link.previously,
      tagTitle: link.previously ? `Previously under ${link.previously}` : undefined,
      onClick: () => open(link),
    }),
    [page, pins, open]
  );

  const visible = useMemo(() => filterNavTree(tree, query), [tree, query]);

  const sections = useMemo<AppMenuSection[]>(
    () =>
      visible.map((section) => ({
        id: section.id,
        header: section.header,
        icon: section.icon,
        open: openIds.includes(section.id),
        links: section.links.map((link) => toMenuLink(section, link)),
      })),
    [visible, openIds, toMenuLink]
  );

  const pinned = useMemo<AppMenuLink[]>(
    () =>
      pins.flatMap((action) =>
        tree.flatMap((section) =>
          section.links
            .filter((link) => link.action === action)
            .map((link) => toMenuLink(section, link))
        )
      ),
    [pins, tree, toMenuLink]
  );

  const openFirstMatch = useCallback((): void => {
    const first = visible[0]?.links[0];
    if (first && query.trim() !== "") open(first);
  }, [visible, query, open]);

  return (
    <AppMenu
      sections={sections}
      onToggleSection={onToggleSection}
      pinned={pinned}
      onTogglePin={onTogglePin}
      search={{
        value: query,
        onChange: setQuery,
        onSubmit: openFirstMatch,
        inputRef,
        boxRef,
        hint: "/",
      }}
      emptyText={`No tools match "${query.trim()}". Try a section name such as Dataloy or FDTR.`}
    />
  );
}

export default Sidebar;
